import { z } from 'zod';

/**
 * Allowed page sizes — RN-03 (EU-149): default is 10. The QA reported the
 * old 11 default as a divergence ("Listagem exibe 10 linhas por página").
 * 25/50/100 follow the same options the companies listing exposes.
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

export const SORT_KEYS = ['name', 'status', 'createdAt', 'updatedAt'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/**
 * Zod schema for `/programs` URL search params. Used as TanStack Router's
 * `validateSearch` and as the queryKey input for TanStack Query — the URL
 * is the single source of truth for table state (page, search, sort).
 *
 * `.catch()` on every field means a malformed value (e.g. `?page=abc`)
 * silently falls back to its default instead of throwing.
 */
export const ProgramsSearchSchema = z
  .object({
    companyId: z.string().uuid().optional().catch(undefined),
    sort: z.enum(SORT_KEYS).optional().catch(undefined),
    dir: z.enum(SORT_DIRECTIONS).catch('desc').default('desc'),
    search: z.string().optional().catch(undefined),
    page: z.coerce.number().int().positive().catch(1).default(1),
    pageSize: z
      .union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)])
      .catch(10)
      .default(10),
  })
  .strip();

export type ProgramsSearch = z.infer<typeof ProgramsSearchSchema>;

export const DEFAULT_PROGRAMS_SEARCH: ProgramsSearch = {
  dir: 'desc',
  page: 1,
  pageSize: 10,
};
